import React, { useEffect, useMemo, useState } from "react";
import {
  FaChevronLeft,
  FaChevronRight,
  FaEdit,
  FaEye,
  FaMedal,
} from "react-icons/fa";
import { FaTableList } from "react-icons/fa6";
import { GiPodium } from "react-icons/gi";

import {
  formatShortDate,
  getAdherentFullName,
  getResultKey,
  getStatusClass,
  getStatusLabel,
  parseResultTokens,
} from "../utils/groupResultsUtils";

const PAGE_SIZE_OPTIONS = [25, 50, 100];

function ResultsBadge({ value }) {
  if (!value) {
    return (
      <span className="inline-flex min-h-8 items-center justify-center rounded-2xl bg-slate-100 px-3 text-xs font-bold text-slate-400 dark:bg-white/10 dark:text-white/35">
        —
      </span>
    );
  }

  return (
    <span className="inline-flex min-h-8 items-center justify-center rounded-2xl bg-slate-900 px-3 text-sm font-bold tracking-[0.2em] text-white dark:bg-white/10">
      {value}
    </span>
  );
}

function FightSummary({ resultats }) {
  const parsed = parseResultTokens(resultats);

  return (
    <div className="flex items-center gap-1 text-[11px] font-bold">
      <span className="rounded-full bg-emerald-100 px-2 py-1 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-200">
        V {parsed.wins}
      </span>
      <span className="rounded-full bg-red-100 px-2 py-1 text-red-700 dark:bg-red-500/10 dark:text-red-200">
        D {parsed.losses}
      </span>
      <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600 dark:bg-white/10 dark:text-white/60">
        C {String(resultats || "").length}
      </span>
    </div>
  );
}

function CoachResultsTable({
  results = [],
  savingKey,
  onEdit,
  onOpenImage,
}) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const totalPages = Math.max(1, Math.ceil(results.length / pageSize));

  useEffect(() => {
    setPage(1);
  }, [results.length, pageSize]);

  const visibleResults = useMemo(() => {
    const start = (page - 1) * pageSize;
    return results.slice(start, start + pageSize);
  }, [page, pageSize, results]);

  const startIndex = results.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIndex = Math.min(page * pageSize, results.length);

  const goPrevious = () => {
    setPage((prev) => Math.max(1, prev - 1));
  };

  const goNext = () => {
    setPage((prev) => Math.min(totalPages, prev + 1));
  };

  return (
    <section className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-xl shadow-slate-200/60 dark:border-white/10 dark:bg-slate-900 dark:shadow-black/20">
      <div className="flex flex-col gap-3 border-b border-slate-200 p-4 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-orange-500">
            Tableau des résultats
          </p>

          <h2 className="mt-1 font-titre text-xl font-bold text-slate-950 dark:text-white">
            {results.length} participation{results.length > 1 ? "s" : ""}
          </h2>
        </div>

        <div className="flex items-center gap-2">
          <select
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
            className="h-10 rounded-2xl border border-slate-200 bg-slate-50 px-3 text-xs font-bold text-slate-700 outline-none transition focus:border-orange-300 focus:ring-4 focus:ring-orange-100 dark:border-white/10 dark:bg-slate-950 dark:text-white dark:focus:ring-orange-500/10"
          >
            {PAGE_SIZE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option} / page
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[980px] border-collapse text-left">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50 text-[11px] font-bold uppercase tracking-[0.14em] text-slate-400 dark:border-white/10 dark:bg-white/[0.03]">
              <th className="sticky left-0 z-10 bg-slate-50 px-4 py-3 dark:bg-slate-900">
                Judoka
              </th>
              <th className="px-4 py-3">Groupe</th>
              <th className="px-4 py-3">Compétition</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Résultats</th>
              {/* <th className="px-4 py-3">Bilan</th> */}
              <th className="px-4 py-3">Classement</th>
              <th className="px-4 py-3">Statut</th>
              <th className="px-4 py-3">Docs</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100 dark:divide-white/10">
            {visibleResults.map((result) => {
              const { adherent, group, event } = result;
              const adherentEvent = event?.AdherentEvent || {};
              const rowKey = getResultKey(result);
              const isSaving = savingKey === "edit-result";

              return (
                <tr
                  key={`${result.groupId}-${rowKey}`}
                  className="text-sm transition hover:bg-orange-50/60 dark:hover:bg-white/[0.03]"
                >
                  <td className="sticky left-0 z-10 bg-white px-4 py-3 dark:bg-slate-900">
                    <div className="flex min-w-[180px] items-center gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-slate-100 text-slate-500 dark:bg-white/10 dark:text-white/60">
                        <FaMedal />
                      </div>

                      <div className="min-w-0">
                        <p className="truncate font-bold text-slate-950 dark:text-white">
                          {getAdherentFullName(adherent)}
                        </p>
                        <p className="mt-0.5 text-xs font-medium text-slate-400">
                          ID {adherent?.id}
                        </p>
                      </div>
                    </div>
                  </td>

                  <td className="px-4 py-3">
                    <span className="inline-flex min-h-8 items-center rounded-2xl bg-slate-100 px-3 text-xs font-bold text-slate-600 dark:bg-white/10 dark:text-white/60">
                      {group || "—"}
                    </span>
                  </td>

                  <td className="max-w-[260px] px-4 py-3">
                    <p className="line-clamp-2 font-bold text-slate-800 dark:text-white/85">
                      {event?.titre || "Compétition"}
                    </p>
                  </td>

                  <td className="whitespace-nowrap px-4 py-3 font-semibold text-slate-600 dark:text-white/60">
                    {formatShortDate(event?.start)}
                  </td>

                  <td className="px-4 py-3">
                    <ResultsBadge value={adherentEvent.resultats} />
                  </td>

                  {/* <td className="px-4 py-3">
                    <FightSummary resultats={adherentEvent.resultats} />
                  </td> */}

                  <td className="px-4 py-3">
                    {adherentEvent.classement ? (
                      <span className="inline-flex min-h-8 items-center gap-2 rounded-2xl bg-amber-100 px-3 text-xs font-bold text-amber-700 dark:bg-amber-500/10 dark:text-amber-200">
                        <GiPodium />
                        {adherentEvent.classement}
                      </span>
                    ) : (
                      <span className="text-xs font-bold text-slate-400">
                        —
                      </span>
                    )}
                  </td>

                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex min-h-8 items-center rounded-2xl px-3 text-xs font-bold ${getStatusClass(
                        adherentEvent.status,
                      )}`}
                    >
                      {getStatusLabel(adherentEvent.status)}
                    </span>
                  </td>

                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {adherentEvent.podium_url ? (
                        <button
                          type="button"
                          onClick={() => onOpenImage(adherentEvent.podium_url)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-orange-100 text-orange-600 transition hover:bg-orange-200 dark:bg-orange-500/10 dark:text-orange-200"
                          title="Voir le podium"
                        >
                          <GiPodium />
                        </button>
                      ) : null}

                      {adherentEvent.tableau_url ? (
                        <button
                          type="button"
                          onClick={() =>
                            window.open(adherentEvent.tableau_url, "_blank")
                          }
                          className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-sky-100 text-sky-600 transition hover:bg-sky-200 dark:bg-sky-500/10 dark:text-sky-200"
                          title="Voir le tableau"
                        >
                          <FaTableList />
                        </button>
                      ) : null}

                      {!adherentEvent.podium_url &&
                        !adherentEvent.tableau_url && (
                          <span className="text-xs font-bold text-slate-400">
                            —
                          </span>
                        )}
                    </div>
                  </td>

                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => onEdit(result)}
                      disabled={isSaving}
                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-xs font-bold text-white transition hover:-translate-y-0.5 hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:text-slate-500 dark:bg-white/10 dark:hover:bg-white/15"
                    >
                      <FaEdit />
                      Modifier
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex flex-col gap-3 border-t border-slate-200 p-4 dark:border-white/10 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-center text-xs font-bold text-slate-500 dark:text-white/50 sm:text-left">
          {startIndex} - {endIndex} sur {results.length}
        </p>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center">
          <button
            type="button"
            onClick={goPrevious}
            disabled={page <= 1}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-600 transition hover:border-orange-200 hover:text-orange-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
          >
            <FaChevronLeft />
            Précédent
          </button>

          <button
            type="button"
            onClick={goNext}
            disabled={page >= totalPages}
            className="inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-600 transition hover:border-orange-200 hover:text-orange-600 disabled:cursor-not-allowed disabled:opacity-40 dark:border-white/10 dark:bg-white/[0.05] dark:text-white"
          >
            Suivant
            <FaChevronRight />
          </button>
        </div>
      </div>
    </section>
  );
}

export default CoachResultsTable;